import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { withBase } from './route.util';
import { fetchFiscalYear } from '../store/apis/performanceService';

/**
 * Get default company code from the list of companies.
 * @param {Array} companyList - List of companies.
 * @returns  {string} - The default company code.
 */
const getDefaultCompany = companyList => {
  if (!Array.isArray(companyList) || companyList.length === 0) {
    return null;
  }
  const defaultCompany = companyList?.find(company => company.ISDefault);
  return defaultCompany;
};

/**
 * Decodes the access token and returns its payload.
 * @returns {Object|null} The decoded token payload, or null if invalid.
 */
const getDecodedAccessToken = () => {
  try {
    const token =
      sessionStorage.getItem('accessToken') ||
      localStorage.getItem('accessToken');
    if (!token) return null;
    return jwtDecode(token);
  } catch (error) {
    return null;
  }
};

/**
 * Handles post-login actions such as storing user data and redirecting.
 * @param {Object} data - The response data from the login API.
 * @param {string} data.user - The user object.
 * @param {string} data.accessToken - The access token.
 * @param {string} data.refreshToken - The refresh token.
 * @param {string} [redirectUrl='/dashboard'] - The URL to redirect to after login.
 */
const handlePostLogin = async (
  data,
  redirectUrl = '/dashboard',
  rememberMe = false,
) => {
  try {
    const decoded = jwtDecode(data.accessToken);
    const user = decoded?.user;

    // Set Remember Me in locaStorage to check if user has rememberMe
    localStorage.setItem('rememberMe', rememberMe);

    if (rememberMe) {
      // Store user data and tokens in localStorage
      localStorage.setItem('userId', user?.employeeId);
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem(
        'companyCode',
        getDefaultCompany(user?.companyList || [])?.Code,
      );
    } else {
      // Store user data and tokens in sessionStorage
      localStorage.removeItem('userId');
      sessionStorage.setItem('accessToken', data.accessToken);
      sessionStorage.setItem('refreshToken', data.refreshToken);

      sessionStorage.setItem(
        'companyCode',
        getDefaultCompany(user?.companyList || [])?.Code,
      );
    }

    const fiscalYear = await fetchFiscalYear();
    sessionStorage.setItem('fiscalYear', JSON.stringify(fiscalYear?.result));

    // Redirect to the specified URL
    if (user?.isDefaultDashboard === 'H') {
      window.location.href = withBase('/hr/dashboard');
    } else {
      window.location.href = withBase(redirectUrl);
    }
  } catch (_error) {
    // Handle any errors that occur during localStorage operations
    toast.error('An error occurred while logging in. Please try again.', {
      className: 'toast-error',
    });
  }
};

/**
 * Retrieves the authenticated user's details from localStorage.
 * @returns {Object|null} The user object if it exists, or null if not found.
 */
const getAuthUser = () => {
  try {
    const token = getDecodedAccessToken();
    const user = token?.user;
    return (
      user ?? {
        userId: null,
        companyList: [],
        menus: [],
        privileges: [],
        userFullName: 'Guest',
      }
    );
  } catch (_error) {
    return { companyList: [], userFullName: 'Guest' };
  }
};

/**
 * Retrieves the authenticated user's details from localStorage.
 * @returns {Object|null} The user object if it exists, or null if not found.
 */
const getPrivileges = () => {
  try {
    const user = getAuthUser();
    const privileges = user?.privileges;
    return privileges ?? [];
  } catch (_error) {
    return { companyList: [], userFullName: 'Guest' };
  }
};

/**
 * Retrieves the authenticated user's details from localStorage.
 * @returns {Object|null} The user object if it exists, or null if not found.
 */
const getMenus = () => {
  try {
    const user = getAuthUser();
    const menus = user?.menus;
    if (Array.isArray(menus) && menus?.length > 0) {
      const menuItems = menus.map(mn => ({
        menuLabel: mn?.MenuLabel,
        displayOrder: mn?.DisplayOrder,
        module: mn?.Module,
      }));
      return menuItems;
    }
    return [];
  } catch (_error) {
    return { companyList: [], userFullName: 'Guest' };
  }
};

/**
 * Get Remeber Me status from localStorage
 * @returns {boolean} - True if Remember Me is enabled, false otherwise.
 */
const getRememberMe = () => {
  const rememberMe = localStorage.getItem('rememberMe');
  return rememberMe === 'true';
};

/**
 * Get data from localStorage or sessionStorage based on Remember Me status
 * @param {*} key
 * @returns
 */
const getDataFromStorage = key => {
  if (getRememberMe()) {
    return JSON.parse(localStorage.getItem(key));
  }
  return JSON.parse(sessionStorage.getItem(key));
};

/**
 * Set data to localStorage or sessionStorage based on Remember Me status
 * @param {*} key
 * @param {*} data
 */
const setDataToStorage = (key, data) => {
  if (getRememberMe()) {
    localStorage.setItem(key, JSON.stringify(data));
  } else {
    sessionStorage.setItem(key, JSON.stringify(data));
  }
};

/**
 * Get all the impersonate users from localStorage or sessionStorage
 * @returns {Array} - Array of impersonate users.
 */
const getImpersonateUsers = () => getDataFromStorage('impersonateUsers') || [];

/**
 * Sets the impersonate users in localStorage or sessionStorage.
 * @param {Array} impersonateUsers - Array of impersonate users.
 */
const setImpersonateUsers = impersonateUsers =>
  setDataToStorage('impersonateUsers', impersonateUsers);

/**
 * Reset selected for all impersonate users.
 * @param {Array} impersonateUsers - Array of impersonate users.
 */
const resetSelectedImpersonateUsers = () => {
  const impersonateUsersList = getImpersonateUsers();
  if (Array.isArray(impersonateUsersList) && impersonateUsersList.length > 0) {
    impersonateUsersList.forEach(user => {
      user.selected = false;
    });
    setImpersonateUsers(impersonateUsersList);
  }
};

/**
 * Adds an impersonate user to the storage.
 * @param {*} employeeDetails
 */
const addImpersonateUser = employeeDetails => {
  let flag = true;
  if (
    employeeDetails?.employeeId === getAuthUser()?.userName.trim().toLowerCase()
  ) {
    resetSelectedImpersonateUsers();
    const impersonateUsers = getImpersonateUsers();
    if (impersonateUsers && impersonateUsers?.length > 0) {
      impersonateUsers.forEach(user => {
        user.selected = user?.employeeId === employeeDetails?.employeeId;
      });
      setImpersonateUsers(impersonateUsers);
    }
    return flag;
  }
  const impersonateUsers = getImpersonateUsers();
  const newUser = {
    ...employeeDetails,
    selected: true,
  };
  if (impersonateUsers && impersonateUsers?.length === 0) {
    impersonateUsers.push({
      employeeId: getAuthUser()?.userName.trim().toLowerCase(),
      userName: getAuthUser()?.userFullName,
      selected: false,
    });
    impersonateUsers.push(newUser);
  } else {
    let existingUserIndex = -1;
    impersonateUsers.forEach((userItem, index) => {
      if (userItem?.employeeId === employeeDetails?.employeeId) {
        existingUserIndex = index;
      }
      userItem.selected = false;
    });
    if (existingUserIndex === -1) {
      if (impersonateUsers && impersonateUsers?.length >= 5) {
        toast.error(
          'You can only add up to 5 impersonate users. Please remove an existing user before adding a new one.',
          {
            className: 'toast-error',
          },
        );
        flag = false;
      } else {
        impersonateUsers.push(newUser);
      }
    } else {
      impersonateUsers[existingUserIndex].selected = true;
    }
  }
  setImpersonateUsers(impersonateUsers);
  return flag;
};

/**
 * Delete an impersonate user from the storage.
 * @param {*} employeeDetails
 */
const deleteImpersonateUser = employeeDetails => {
  const impersonateUsers = getImpersonateUsers();
  const updatedUsers = impersonateUsers.filter(
    user => user?.employeeId !== employeeDetails?.employeeId,
  );
  if (updatedUsers?.length === 1) {
    setImpersonateUsers(null);
  } else {
    setImpersonateUsers(updatedUsers);
  }
};

/**
 * Get Default Base Currency
 */
const getDefaultBaseCurrency = () => {
  const user = getAuthUser();
  const company = getDefaultCompany(user?.companyList);
  return company?.BaseCurr?.trim() || '';
};

export {
  handlePostLogin,
  getDecodedAccessToken,
  getAuthUser,
  getMenus,
  getPrivileges,
  getDefaultCompany,
  getRememberMe,
  getImpersonateUsers,
  addImpersonateUser,
  deleteImpersonateUser,
  getDefaultBaseCurrency,
};
